import { InfoCircleOutlined } from '@ant-design/icons';
import { Divider, Input, Popover, Tag, Tooltip } from 'antd';
import i18n from 'i18next';
import { cloneDeep, get, isNil, set, truncate, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import TagChooser from 'components/choosers/TagChooser';
import { WorkContainerArchivedChooser } from 'components/choosers/WorkContainerArchivedChooser';
import { WorkContainerChooser } from 'components/choosers/WorkContainerChooser';
import * as ATTACHMENTS_COLUMNS from 'components/data/attachments';
import {
  type TAtlasTaskWithTypeName,
  type TDisplayColumnData,
  type TEditColumnData,
} from 'components/data/types';
import {
  getNestedMakeModelAssetFilterColumnProps,
  SiteFilter,
} from 'components/DataTable/customFilters';
import { StyledTable } from 'components/DataTable/DataTable.style';
import { StatusTag } from 'components/StatusTag';
import {
  getAssetColumnCopy,
  getOrderedAncestors,
  getUniqueIdentifier,
} from 'horizon/components/Assets/AssetDetails/helpers';
import { PropagationTag } from 'horizon/components/Damages/PropagationTag';
import {
  DamageLink,
  getObservationGroupByInspectionId,
  getPrimaryObservationGroupForDamage,
  getTaskTargetInspectionId,
  propagationTypeMapping,
  resolveGroupPropagation,
} from 'horizon/components/Damages/utils';
import { EWorkContainerArchiveOptions } from 'horizon/routes/WorkOrders/Tasks/TaskList/types';
import {
  type AtlasGqlAsset,
  type AtlasGqlInspection,
  type AtlasGqlInspectionTask,
  type AtlasGqlOrganization,
  type AtlasGqlTask,
  type AtlasGqlWorkCampaign,
  type AtlasGqlWorkContainer,
  type AtlasGqlWorkOrder,
  AtlasGqlMaintenanceActivityStatus,
} from 'types/atlas-graphql';
import {
  TASK_EDIT_COMPLETED_DATE,
  TASK_EDIT_DESCRIPTION,
  TASK_EDIT_DUE_DATE,
  TASK_EDIT_STATUS,
  TASK_EDIT_TAGS,
  TASK_EDIT_VENDOR,
  TASK_EDIT_WORK_ORDER,
} from 'utils/access-control/rules';
import {
  STANDARD_COLUMN_SCROLL_Y,
  TABLE_FILTER_OPERATORS,
  TABLE_SCROLL_WIDTH_SMALL,
  WORK_ORDER,
} from 'utils/constants';
import { formatDate, startCase } from 'utils/format';
import naturalSort from 'utils/naturalSort';
import { useVendorOrgFilterOptions } from 'utils/organization';

import {
  ASSET_TYPENAMES,
  getFilter,
  getFilterDropdown,
  getOnFilter,
  None,
  sortBy,
  truncateTooltipper,
} from '../helpers';

import {
  completedDate as commonCompletedDate,
  description as commonDescription,
  dueDate as commonDueDate,
  shortDescription as commonShortDescription,
  status as commonStatus,
} from './common';
import { CenteredSpan, RepeatingTaskIcon } from './tasks.style';

// Takes a task and allows sorting based on the turbine or site,
// whether the task is directly on a turbine or site, or
// on a component that has a turbine and / or site as a parent.
// 'Site' and 'Turbine' are the only supported parent asset types.
export const getSortableParentAssetLabel = (task: any, parentAssetType: 'Site' | 'Turbine') => {
  const asset = task?.asset;
  if (!asset) {
    return;
  }
  const { __typename: assetType } = asset;
  let label: string | undefined = undefined;

  // if task is directly on the turbine or site, get the the turbine or site's label
  if (assetType === parentAssetType) {
    label = asset.uniqueIdLabel;
  } else {
    // if task is on a component that has a turbine and / or site as a parent,
    // get that parent asset's label
    const { ancestors } = asset;
    const parentAsset = ancestors.find((record: any) => record.__typename === parentAssetType);
    if (parentAsset) {
      label = parentAsset.uniqueIdLabel;
    }
  }
  return label;
};

// We want to use this sorter only if the task is directly on a component,
// not on a site or turbine.
const getSortableComponentLabel = (task: any) => {
  const asset = task?.asset;
  if (!asset) {
    return;
  }
  const { __typename: assetType } = asset;
  let label: string | undefined = undefined;

  if (assetType !== 'Site' && assetType !== 'Turbine') {
    label = asset.uniqueIdLabel;
  }
  return label;
};

export {
  clientDeliveryDate,
  clientDeliveryStatus,
  created,
  createdAndCreatedBy,
  createdBy,
  createdFrom,
  deliveryDate,
  deliveryStatus,
  getForOrganization,
  getMostRecentDeliveryForTargetCustomer,
} from './common';

export {
  assetBlade as blade,
  assetSite as site,
  assetTurbine as turbine,
  atlasAssetBlade as atlasBlade,
  location,
  removeAssetLink,
} from './taskAssets';

export const shortDescription = {
  ...commonShortDescription,
  onHeaderCell: () => ({
    'data-testid': 'tasks-description-header',
  }),
  defaultFilterOperator: TABLE_FILTER_OPERATORS.CONTAINS,
  editRule: TASK_EDIT_DESCRIPTION,
};

export const shortDescriptionBulkEdit = (disableCheckbox?: boolean) => {
  return {
    ...shortDescription,
    disableCheckbox: disableCheckbox,
  };
};

export const description = {
  ...commonDescription,
  editRule: TASK_EDIT_DESCRIPTION,
};

export const dueDate = {
  ...commonDueDate,
  onHeaderCell: () => ({
    'data-testid': 'tasks-due-date-header',
  }),
  editRule: TASK_EDIT_DUE_DATE,
};

export const dueDateBulkEdit = (disableCheckbox?: boolean) => {
  return {
    ...dueDate,
    // The basic dueDate column uses the slug value in snake_case, but the edit
    // task input expects the field key in camelCase.
    key: 'dueDate',
    disableCheckbox: disableCheckbox,
  };
};

export const completedDate = {
  ...commonCompletedDate,
  key: 'completed_date',
  onHeaderCell: () => ({
    'data-testid': 'tasks-completed-date-header',
  }),
  editRule: TASK_EDIT_COMPLETED_DATE,
};

export const completedDateBulkEdit = (disableCheckbox?: boolean) => {
  return {
    ...completedDate,
    // Have to override the key here as the basic completedDate column uses the slug value in snake_case
    // But the edit task input expects a field in camelCase as they field key.
    key: 'completedDate',
    disableCheckbox: disableCheckbox,
  };
};

export const status = {
  ...commonStatus,
  onHeaderCell: () => ({
    'data-testid': 'tasks-status-header',
  }),
  editRule: TASK_EDIT_STATUS,
};

export const statusBulkEdit = (disableCheckbox?: boolean) => {
  return {
    ...status,
    disableCheckbox: disableCheckbox,
  };
};

export const damageId = {
  key: 'damage.id',
  dataIndex: ['damage', 'id'],
  title: i18n.t('table.damage', 'Damage'),
  filterProperty: 'damage.id',
  get: (record: any) => get(record, ['damage', 'id']),
  render(damageId: any, task: any) {
    const { damage = {} } = task || {};
    const { number = {}, observations: [{ picture: { id: pictureId = '' } = {} } = {}] = [] } =
      damage;
    return [damageId, number, pictureId].every(Boolean) ? (
      <Link to={`/pictures/${pictureId}?damages=${damageId}`}>{number}</Link>
    ) : (
      <None />
    );
  },
};

export const damageDate = {
  key: 'damage.attrs.Date',
  dataIndex: ['damage', 'attrs', 'Date'],
  title: i18n.t('table.damageDate', 'Damage Date'),
  filterProperty: 'damage.attrs.Date',
  get: (record: any) => get(record, ['damage', 'attrs', 'Date']),
  render(damageDate: any) {
    return damageDate ? formatDate(damageDate) : <None />;
  },
};

export const taskNumber = {
  key: 'taskNumber',
  dataIndex: 'number',
  title: i18n.t('table.task', 'Task'),
  onHeaderCell: () => ({
    'data-testid': 'tasks-number-header',
  }),
  filterProperty: 'number',
  get: (record: any) => get(record, 'number'),
  render: (number: any, task: AtlasGqlWorkContainer) => {
    const { id } = task || {};
    return [id, number].every(Boolean) ? <Link to={`/tasks/${id}`}>{number}</Link> : <None />;
  },
};

export const workOrder = {
  key: 'workOrder.name',
  title: i18n.t('table.workOrder', 'Work Order'),
  dataIndex: ['workOrder'],
  sorter: sortBy('workOrder.name'),
  filterProperty: 'workOrder.name',
  get: (record: any) => get(record, ['workOrder', 'name']),
  render: (workOrder: any) => {
    const { id, name } = workOrder || {};
    return [id, name].every(Boolean) ? (
      <Link to={`/work-orders/${id}`}>{truncateTooltipper(name)}</Link>
    ) : (
      <None />
    );
  },
  editRule: TASK_EDIT_WORK_ORDER,
  edit(record: any, { onChange, ...props }: any) {
    const { workOrder } = record;
    // handle unplanned tasks where workOrder is null
    const number = workOrder ? workOrder.number : null;
    const id = workOrder ? workOrder.id : null;

    return (
      <WorkContainerChooser
        defaultValue={number ? { key: number } : undefined}
        containerId={id}
        type={WORK_ORDER}
        onChange={
          onChange &&
          ((value: any) => {
            onChange({
              parentId: value,
            });
          })
        }
        {...props}
      />
    );
  },
};

export const tags = {
  title: i18n.t('table.tags', 'Tags'),
  onHeaderCell: () => ({
    'data-testid': 'tasks-tags-header',
  }),
  dataIndex: 'tags',
  render: (tags: string[]) =>
    tags && tags.length ? tags.map((tag) => <Tag key={tag}>{tag}</Tag>) : <None />,
  getInitialValue: (record: any) => (record.tags ? record.tags : []),
  filterProperty: 'tags',
  onFilter: (filterValue: string, record: AtlasGqlTask): boolean =>
    record.tags?.includes(filterValue) ?? false,
  defaultFilterOperator: TABLE_FILTER_OPERATORS.ARRAY_INCLUDES,
  key: 'tags',
  editRule: TASK_EDIT_TAGS,
  edit(record: any, { onChange, ...props }: any) {
    const { tags: value } = record || {};
    return (
      <TagChooser
        defaultValue={value || []}
        onChange={onChange && ((tags: any) => onChange({ tags }))}
        {...props}
      />
    );
  },
};

export const tagsBulkEdit = (disableCheckbox: boolean) => {
  return {
    ...tags,
    disableCheckbox: disableCheckbox,
  };
};

// FIXME: add a taskTypes query so that these can be retrieved, not hard coded
const taskFilterConfig = {
  key: 'type',
  type: 'string',
  options: ['REPAIR', 'INSPECT', 'INTERNAL_BLADE_INSPECTION', 'OTHER'],
  exactMatch: true,
  values: [],
  filterAttr: undefined,
};

// FIXME JD: Deprecate this column in favor of atlasType as queries get migrated over to Atlas.
export const type = {
  key: 'taskType',
  title: i18n.t('table.taskType', 'Task Type'),
  // FIXME: add a taskTypes query so that these can be retrieved, not hard coded
  filters: [
    { text: i18n.t('task.type.repair', 'Repair'), value: 'REPAIR' },
    { text: i18n.t('task.type.inspect', 'Inspect'), value: 'INSPECT' },
    {
      text: i18n.t('task.type.internalBladeInspection', 'Internal Blade Inspection'),
      value: 'INTERNAL_BLADE_INSPECTION',
    },
    { text: i18n.t('task.type.other', 'Other'), value: 'OTHER' },
    { text: i18n.t('task.type.inspection', 'Inspection'), value: 'INSPECTION' },
  ],
  onFilter: getOnFilter(taskFilterConfig),
  filterDropdown: getFilterDropdown(taskFilterConfig),
  sorter: sortBy('type'),
  get: (record: any) => get(record, 'type'),
  render: (task: any) => {
    const type = task?.type;
    const __typename = task?.__typename;

    const renderType = (t: any) => (t ? startCase(t) : <None />);
    if (type) {
      return renderType(type);
    }
    if (__typename) {
      return renderType(__typename.replace('Task', '').replace('Damage', ''));
    }
    return <None />;
  },
};

export const vendor = {
  key: 'vendor',
  title: i18n.t('table.vendorLegacy', 'Vendor (Legacy)'),
  dataIndex: 'vendor',
  sorter: sortBy('vendor'),
  filterProperty: 'vendor',
  defaultFilterOperator: TABLE_FILTER_OPERATORS.CONTAINS,
  get: (record: any) => get(record, 'vendor'),
  render: (vendor: any) => {
    return vendor ? truncate(vendor, { length: 15 }) : <None />;
  },
  editRule: TASK_EDIT_VENDOR,
  edit(record: any, { onChange, ...props }: any) {
    return (
      <Input
        style={{ maxWidth: '250px' }}
        defaultValue={record.vendor}
        onChange={onChange && ((e) => onChange({ vendor: e.target.value }))}
        {...props}
      />
    );
  },
};

export const archived = (idsToUpdate: string[]): TEditColumnData => {
  return {
    key: EWorkContainerArchiveOptions.archived,
    title: i18n.t('table.archived', 'Archived'),
    get: (record: any) => get(record, EWorkContainerArchiveOptions.archived),
    editRule: TASK_EDIT_WORK_ORDER,
    edit: (record: any, { ...props }: any) => {
      const { disabled } = props;
      return (
        <WorkContainerArchivedChooser
          archivedValue={record.archived}
          disabled={disabled}
          idsToUpdate={idsToUpdate}
          {...props}
        />
      );
    },
  };
};

/*
  Atlas column definitions
*/
export const atlasDamageId: TDisplayColumnData = {
  key: 'damage.id',
  dataIndex: ['damage', 'id'],
  title: i18n.t('table.damage', 'Damage'),
  // FIXME JDJ: Filtering is currently disabled as the Task service cannot filter by Damage name
  // filterProperty: 'damage.id',
  defaultFilterOperator: TABLE_FILTER_OPERATORS.CONTAINS,
  get: (record: any) => get(record, ['damage', 'id']),
  render: (damageId: any, task: any) => {
    const { damage = {} } = task || {};
    const number = damage?.number;
    const observations = damage?.observations;
    const [{ pictureId = '' } = {}] = observations && observations.length ? observations : [];
    return [damageId, number, pictureId].every(Boolean) ? (
      <Link to={`/pictures/${pictureId}?damages=${damageId}`}>{number}</Link>
    ) : (
      <None />
    );
  },
};

export const atlasDamageNumberLink: TDisplayColumnData = {
  key: 'damage',
  dataIndex: ['damage'],
  title: i18n.t('table.damage', 'Damage'),
  // FIXME JDJ: Filtering is currently disabled as the Task service cannot filter by Damage name
  // filterProperty: 'damage.id',
  defaultFilterOperator: TABLE_FILTER_OPERATORS.CONTAINS,
  get: (record: any) => get(record, ['damage']),
  render: (damage: any, _task: any) => {
    const { number, id, observations } = damage ?? {};

    return [id, number, observations].every(Boolean) ? (
      <Link to={`/pictures/${observations[0]?.pictureId}?damages=${id}`}>{number}</Link>
    ) : (
      <None />
    );
  },
};

export const atlasHorizonDamageLink: TDisplayColumnData = {
  key: 'horizonDamage',
  dataIndex: ['horizonDamage'],
  title: i18n.t('table.damage', 'Damage'),
  // FIXME JDJ: Filtering is currently disabled as the Task service cannot filter by Damage name
  // filterProperty: 'horizonDamage.id',
  defaultFilterOperator: TABLE_FILTER_OPERATORS.CONTAINS,
  get: (record: any) => get(record, ['horizonDamage']),
  render: (damage: any, _task: any) => {
    return damage?.id ? <DamageLink damageId={damage?.id} /> : <None />;
  },
};

export const unstyledAtlasHorizonDamageLink: TDisplayColumnData = {
  key: 'horizonDamage',
  dataIndex: ['horizonDamage'],
  title: i18n.t('table.damage', 'Damage'),
  defaultFilterOperator: TABLE_FILTER_OPERATORS.CONTAINS,
  get: (record: any) => get(record, ['horizonDamage']),
  render: (damage: any, _task: any) => {
    return damage?.id ? (
      <Link to={`/damages2/${damage.id}`}>{damage.id.split('-')[0].toUpperCase()}</Link>
    ) : (
      <None />
    );
  },
};

// site columns
export const serverAtlasTaskSites = (
  isCollaborator: boolean = false,
  showFilter: boolean = false
): TDisplayColumnData => {
  return {
    title: i18n.t('table.site', 'Site'),
    key: 'site',
    dataIndex: 'asset',
    onHeaderCell: () => ({
      'data-testid': 'tasks-sites-header',
    }),
    // sorter and filterProperty are needed for sort/filter options to shows up. They are unused otherwise.
    sorter: sortBy('site'),
    ...(showFilter ? { filterDropdown: (props: any) => <SiteFilter {...props} /> } : {}),
    render: (asset: any) => {
      const site =
        asset?.assetType?.name === 'Site'
          ? asset
          : asset?.ancestors?.find((a: any) => a?.assetType?.name === 'Site');

      if (!site) {
        return <None />;
      }

      return isCollaborator ? (
        <span>{site.name}</span>
      ) : (
        <Link key={site.id} to={`/sites/${site.id}/tasks`}>
          {site.name}
        </Link>
      );
    },
  };
};

export const clientAtlasTaskSites = (
  isCollaborator: boolean = false,
  showFilter: boolean = false
): TDisplayColumnData => {
  const serverColumn = serverAtlasTaskSites(isCollaborator, showFilter);
  return {
    ...serverColumn,
    sorter: (taskA: any, taskB: any) => {
      // In some cases the task is directly on a site. In other cases, the task is
      // on a component that has a site as its parent. We need to sort in either case.
      const labelA = getSortableParentAssetLabel(taskA, 'Site');
      const labelB = getSortableParentAssetLabel(taskB, 'Site');
      return labelA || labelB ? naturalSort(labelA, labelB) : 0;
    },
    get: (record: any) => getSortableParentAssetLabel(record, 'Site') || '',
  };
};

// asset columns
export const serverAtlasTaskAssets = (
  tableId: string,
  isCollaborator: boolean = false,
  showFilter: boolean = false
): TDisplayColumnData => {
  return {
    title: i18n.t('table.asset', 'Asset'),
    key: 'asset',
    dataIndex: 'asset',
    onHeaderCell: () => ({
      'data-testid': 'tasks-assets-header',
    }),
    // sorter and filterProperty are needed for sort/filter options to shows up. They are unused otherwise.
    sorter: sortBy('asset'),
    ...(showFilter ? getNestedMakeModelAssetFilterColumnProps(tableId) : {}),
    render: (asset: any) => {
      if (!asset) {
        return <None />;
      }

      return (
        <>
          {getOrderedAncestors(asset)
            .filter((a) => a.assetType?.name === 'Turbine')
            .map((a) => (
              <span key={a.id}>
                {isCollaborator ? (
                  <span>{getUniqueIdentifier(a)}</span>
                ) : (
                  <Link to={`/assets/${a.id}/tasks`}>{getUniqueIdentifier(a)}</Link>
                )}

                <Divider type="vertical" />
              </span>
            ))}
          {isCollaborator ? (
            <span>{getUniqueIdentifier(asset)}</span>
          ) : (
            <Link to={`/assets/${asset.id}/tasks`}>{getUniqueIdentifier(asset)}</Link>
          )}
        </>
      );
    },
  };
};

export const clientAtlasTaskAssets = (
  tableId: string,
  isCollaborator: boolean = false,
  showFilter: boolean = false
): TDisplayColumnData => {
  const serverColumn = serverAtlasTaskAssets(tableId, isCollaborator, showFilter);
  return {
    ...serverColumn,
    sorter: (taskA: any, taskB: any) => {
      const labelA = getAssetColumnCopy(taskA.asset);
      const labelB = getAssetColumnCopy(taskB.asset);
      return labelA || labelB ? naturalSort(labelA, labelB) : 0;
    },
    get: (record: any) => getAssetColumnCopy(record.asset),
  };
};

export const assetStatusColumn: TDisplayColumnData = {
  key: 'assetStatus',
  title: i18n.t('table.assetStatus', 'Asset Status'),
  dataIndex: 'asset',
  onHeaderCell: () => ({
    'data-testid': 'tasks-asset-status-header',
  }),
  render: (asset: AtlasGqlAsset) =>
    asset?.assetType?.name &&
    !['Site', 'Turbine'].includes(asset.assetType.name) &&
    asset?.assetStatus ? (
      <StatusTag value={asset.assetStatus} />
    ) : (
      <None />
    ),
};

// turbine columns
export const serverAtlasTaskTurbines = (isCollaborator: boolean = false): TDisplayColumnData => {
  return {
    title: i18n.t('table.turbine', 'Turbine'),
    key: 'turbine',
    dataIndex: 'asset',
    onHeaderCell: () => ({
      'data-testid': 'tasks-turbines-header',
    }),
    // sorter and filterProperty are needed for sort/filter options to shows up. They are unused otherwise.
    sorter: sortBy('turbine'),
    render: (asset: any) => {
      // No asset return None immediately
      if (!asset) {
        return <None />;
      }

      const { __typename: type, ancestors, name, id, serial } = asset;

      // If the task is on a turbine we'll have the uniqueIdLabel on asset
      if (type === ASSET_TYPENAMES.turbine) {
        return isCollaborator ? (
          <span>{name}</span>
        ) : (
          <Link key={id} to={`/turbines/${id}/tasks`}>
            {name}
            {serial ? ` (${i18n.t('asset.serialAbbreviation', 'SN')}: ${serial})` : ''}
          </Link>
        );
      }

      // If no ancestor somehow return None
      if (!ancestors) {
        return <None />;
      }

      // Otherwise find the turbine information
      const turbine = ancestors.find(
        (record: any) => record.__typename === ASSET_TYPENAMES.turbine
      );
      if (!turbine) {
        return <None />;
      }

      return isCollaborator ? (
        <span>{turbine.name}</span>
      ) : (
        <Link key={turbine.id} to={`/turbines/${turbine.id}/tasks`}>
          {turbine.name}
        </Link>
      );
    },
  };
};

export const clientAtlasTaskTurbines = (isCollaborator: boolean = false): TDisplayColumnData => {
  const serverColumn = serverAtlasTaskTurbines(isCollaborator);
  return {
    ...serverColumn,
    sorter: (taskA: any, taskB: any) => {
      // In some cases the task is directly on a turbine. In other cases, the task is
      // on a component that has a turbine as its parent. We need to sort in either case.
      const labelA = getSortableParentAssetLabel(taskA, 'Turbine');
      const labelB = getSortableParentAssetLabel(taskB, 'Turbine');
      return labelA || labelB ? naturalSort(labelA, labelB) : 0;
    },
    get: (record: any) => getSortableParentAssetLabel(record, 'Turbine'),
  };
};

export const turbineStatusColumn: TDisplayColumnData = {
  key: 'turbineStatus',
  title: i18n.t('table.turbineStatus', 'Turbine Status'),
  dataIndex: 'asset',
  onHeaderCell: () => ({
    'data-testid': 'tasks-turbine-status-header',
  }),
  render: (asset: AtlasGqlAsset) => {
    // If the base asset is a turbine, return the turbine status
    if (asset?.assetType?.name && asset.assetType.name === 'Turbine' && asset?.assetStatus) {
      return <StatusTag value={asset.assetStatus} />;
    }
    // If the base asset is a component, return the parent turbine status
    if (asset?.ancestors?.find((ancestor) => ancestor.assetType?.name === 'Turbine')) {
      const parentTurbineStatus = asset?.ancestors?.find(
        (ancestor) => ancestor.assetType?.name === 'Turbine'
      )?.assetStatus;
      return parentTurbineStatus ? <StatusTag value={parentTurbineStatus} /> : <None />;
    }
    return <None />;
  },
};

// component columns
export const serverAtlasTaskComponent = (isCollaborator: boolean = false): TDisplayColumnData => {
  return {
    title: i18n.t('table.component', 'Component'),
    key: 'component',
    dataIndex: 'asset',
    onHeaderCell: () => ({
      'data-testid': 'tasks-component-header',
    }),
    sorter: sortBy('asset'),
    render: (asset: any) => {
      if (!asset) {
        return <None />;
      }

      const type = asset?.assetType?.name ?? asset?.__typename;

      // If we're a Site or a Turbine we're displaying component info in those columns
      if (!asset || !type || type === 'Site' || type === 'Turbine') {
        return <None />;
      }

      // Build Blades and/or generic assets link
      const { id, uniqueIdLabel } = asset;
      return isCollaborator ? (
        <span>{uniqueIdLabel}</span>
      ) : (
        <Link
          key={id}
          to={`${type === ASSET_TYPENAMES.blade ? '/blades/' : '/assets/'}${id}/tasks`}
        >
          {uniqueIdLabel}
        </Link>
      );
    },
  };
};

export const clientAtlasTaskComponent = (isCollaborator: boolean = false): TDisplayColumnData => {
  const serverSide = serverAtlasTaskComponent(isCollaborator);
  return {
    ...serverSide,
    sorter: (taskA: any, taskB: any) => {
      const labelA = getSortableComponentLabel(taskA);
      const labelB = getSortableComponentLabel(taskB);
      return labelA || labelB ? naturalSort(labelA, labelB) : 0;
    },
    get: (record: any) => getSortableComponentLabel(record),
  };
};

export const componentStatusColumn: TDisplayColumnData = {
  key: 'componentStatus',
  title: i18n.t('table.componentStatus', 'Component Status'),
  dataIndex: 'asset',
  onHeaderCell: () => ({
    'data-testid': 'tasks-component-status-header',
  }),
  render: (asset: AtlasGqlAsset) =>
    asset?.assetType?.name &&
    !['Site', 'Turbine'].includes(asset.assetType.name) &&
    asset?.assetStatus ? (
      <StatusTag value={asset.assetStatus} />
    ) : (
      <None />
    ),
};

const atlasType: TDisplayColumnData = {
  key: 'type',
  title: i18n.t('table.taskType', 'Task Type'),
  get: (record: any) => get(record, 'type'),
  render: (task: TAtlasTaskWithTypeName) => {
    const type = task?.type;
    const __typename = task?.__typename;

    const renderType = (t: any) =>
      t ? startCase(t.replace('Task', '').replace('Damage', '')) : <None />;
    if (type) {
      return renderType(type?.name);
    }
    if (__typename) {
      return renderType(__typename);
    }
    return <None />;
  },
};

export const serverAtlasType: TDisplayColumnData = {
  ...atlasType,
  onHeaderCell: () => ({
    'data-testid': 'tasks-type-header-server',
  }),
  // FIXME: add a taskTypes query so that these can be retrieved, not hard coded
  filters: [
    { text: i18n.t('task.type.repair', 'Repair'), value: 'REPAIR_TASK' },
    { text: i18n.t('task.type.inspect', 'Inspect'), value: 'INSPECT_TASK' },
    {
      text: i18n.t('task.type.internalBladeInspection', 'Internal Blade Inspection'),
      value: 'INTERNAL_BLADE_INSPECTION_TASK',
    },
    { text: i18n.t('task.type.other', 'Other'), value: 'OTHER_TASK' },
    { text: i18n.t('task.type.inspection', 'Inspection'), value: 'INSPECTION_TASK' },
  ],
  filterDropdown: getFilterDropdown({
    key: 'type',
    type: 'string',
  }),
};

export const clientAtlasType: TDisplayColumnData = {
  ...atlasType,
  onHeaderCell: () => ({
    'data-testid': 'tasks-type-header',
  }),
  filters: [
    { text: i18n.t('task.type.repair', 'Repair'), value: 'REPAIR TASK' },
    { text: i18n.t('task.type.inspect', 'Inspect'), value: 'INSPECT TASK' },
    {
      text: i18n.t('task.type.internalBladeInspection', 'Internal Blade Inspection'),
      value: 'INTERNAL BLADE INSPECTION TASK',
    },
    { text: i18n.t('task.type.other', 'Other'), value: 'OTHER TASK' },
    { text: i18n.t('task.type.inspection', 'Inspection'), value: 'INSPECTION TASK' },
  ],
  onFilter: getOnFilter({ ...taskFilterConfig, key: 'type.name' }),
  filterDropdown: getFilterDropdown(taskFilterConfig),
  sorter: sortBy('type.name'),
};

export const taskLegacyAtlasVendorColumn: TDisplayColumnData = {
  key: 'vendor',
  title: i18n.t('table.vendorLegacy', 'Vendor (Legacy)'),
  onHeaderCell: () => ({
    'data-testid': 'tasks-vendor-header',
  }),
  dataIndex: 'vendor',
  sorter: sortBy('vendor'),
  filterProperty: 'vendor',
  get: (record: any) => get(record, 'vendor'),
  render: (vendor: string) => {
    return vendor ? truncate(vendor, { length: 15 }) : <None />;
  },
};

// this vendor org column is for non-work order tasks only
export const taskVendorHeaderColumn = {
  key: 'taskVendorOrg',
  title: i18n.t('table.vendor', 'Vendor'),
  dataIndex: ['parent'],
  render: (workOrder?: AtlasGqlWorkOrder | null) => {
    const infoIcon = <InfoCircleOutlined data-testid="task-vendor-org-info" />;

    if (!workOrder) {
      return (
        <>
          <None />{' '}
          <Tooltip
            children={infoIcon}
            title={
              <span data-testid="task-vendor-org-info-tooltip">
                {i18n.t(
                  'task.vendorOrgTooltip',
                  'Vendor organization is determined by work order. You may add this task to a work order and enter the vendor organization there.'
                )}
              </span>
            }
          />
        </>
      );
    }

    return (
      <>
        {workOrder.vendorOrganization?.name || <None />}{' '}
        <Tooltip
          children={infoIcon}
          title={
            <span data-testid="task-vendor-org-info-tooltip">
              {i18n.t(
                'task.vendorOrgWorkOrderTooltipPrefix',
                "Vendor organization is determined by the task's "
              )}
              <Link data-testid="task-vendor-org-info-link" to={`/work-orders/${workOrder?.id}`}>
                {i18n.t('task.workOrderLinkText', 'work order')}
              </Link>
              {i18n.t(
                'task.vendorOrgWorkOrderTooltipSuffix',
                '. You may update the value from the work order.'
              )}
            </span>
          }
        />
      </>
    );
  },
};

export function useTaskVendorOrgColumn() {
  const { t } = useTranslation();
  const { vendorOptions } = useVendorOrgFilterOptions();

  const vendorOrg: TDisplayColumnData = {
    key: 'vendorName',
    title: t('table.vendor', 'Vendor'),
    onHeaderCell: () => ({
      'data-testid': 'tasks-vendor-org-header',
    }),
    dataIndex: ['parent'],
    sorter: sortBy('parent.vendorOrganization.name'),
    filters: vendorOptions,
    filterProperty: 'parent.vendorOrganization.name',
    filterDropdown: getFilterDropdown({
      includeNoneOption: true,
      noneOptionValue: 'IS_NULL',
      type: 'string',
      values: vendorOptions,
    }),
    render: (parent: AtlasGqlWorkOrder) => {
      return parent?.vendorOrganization?.name ?? <None />;
    },
  };

  return vendorOrg;
}

export const atlasWorkOrder = (columnLength?: number): TEditColumnData => {
  return {
    // NOTE: keys must match the filter defined in the tabs.ts TAB_LIST filter or the Unplanned tab will reset when advancing to the next page.
    key: 'parentName',
    title: i18n.t('table.workOrder', 'Work Order'),
    dataIndex: ['parent'],
    sorter: sortBy('parent.name'),
    filterProperty: 'parent.name',
    get: (record: any) => get(record, ['parent', 'name']),
    render: (parent: AtlasGqlWorkOrder) => {
      const { id, name } = parent || {};
      return [id, name].every(Boolean) ? (
        <Link to={`/work-orders/${id}`}>
          {truncateTooltipper(name, columnLength ? columnLength : undefined)}
        </Link>
      ) : (
        <None />
      );
    },
    // This column is used for task detail fields, so it needs to be editable
    editRule: TASK_EDIT_WORK_ORDER,
    edit(record: any, { onChange, ...props }: any) {
      const { parent: workOrder } = record;
      // handle unplanned tasks where workOrder is null
      const number = workOrder ? workOrder.number : null;
      const { disabled } = props;

      return (
        <WorkContainerChooser
          defaultValue={number ? { key: number } : undefined}
          containerId={'atlas-work-order'}
          type={WORK_ORDER}
          onChange={
            onChange &&
            ((value: any) => {
              onChange({
                parentId: value,
              });
            })
          }
          disabled={disabled}
          {...props}
        />
      );
    },
    onHeaderCell: () => ({
      'data-testid': 'tasks-work-order-atlas',
    }),
  };
};

export const atlasCampaign = {
  key: 'campaignName',
  title: i18n.t('table.campaign', 'Campaign'),
  dataIndex: ['parent', 'parent'],
  sorter: sortBy('parent.parent.name'),
  filterProperty: 'parent.parent.name',
  defaultFilterOperator: TABLE_FILTER_OPERATORS.EQUALS,
  onFilter: getOnFilter({
    key: 'parent.parent.name',
    type: 'string',
    filterAttr: undefined,
    exactMatch: true,
    values: [],
    includeNoneOption: false,
  }),
  get: (record: any) => get(record, ['parent', 'parent', 'name']),
  render: (campaign: AtlasGqlWorkCampaign) => {
    const { id, name } = campaign || {};
    return [id, name].every(Boolean) ? (
      <Link to={`/campaigns/${id}`}>{truncateTooltipper(name)}</Link>
    ) : (
      <None />
    );
  },
};

const propagationSortOrder = [
  'Repair Failed',
  'Repair Incomplete',
  'Formed',
  'Propagation - Heavy',
  'Propagation - Medium',
  'Propagation - Light',
  'No Change',
];

const getPropagation = (record: any) => {
  const { horizonDamage, targets } = record || {};

  if (!horizonDamage?.id || !horizonDamage?.propagations?.length) {
    return null;
  }

  const inspectionId = targets ? getTaskTargetInspectionId(targets) : undefined;

  const primaryGroup = getPrimaryObservationGroupForDamage(horizonDamage);

  const targetGroup = inspectionId
    ? getObservationGroupByInspectionId(horizonDamage, inspectionId)
    : primaryGroup;

  return resolveGroupPropagation(
    horizonDamage.propagations,
    horizonDamage.observationGroups ?? [],
    targetGroup ?? primaryGroup
  );
};

const getPropagationType = (record: any) => {
  const type = getPropagation(record)?.type;
  return type ? propagationTypeMapping[type] : undefined;
};

const getSortIndex = (record: any) => {
  const label = getPropagationType(record);
  const idx = propagationSortOrder.indexOf(label ?? '');
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

const propagationTypeConfig = {
  key: 'propagationType',
  type: 'string',
  exactMatch: false,
  values: [
    ...Object.keys(propagationTypeMapping).map((key) => ({
      id: key,
      label: propagationTypeMapping[key],
    })),
    { id: '', label: <i>{i18n.t('common.none', 'none')}</i> },
  ],
  includeNoneOption: true,
};

export const propagationType = {
  key: 'propagationType',
  title: i18n.t('table.propagationType', 'Propagation Type'),
  sorter: (a: any, b: any) => getSortIndex(a) - getSortIndex(b),
  filters: getFilter(propagationTypeConfig),
  filterDropdown: getFilterDropdown(propagationTypeConfig),
  onFilter: (filterValue: string, record: any) =>
    getPropagationType(record) === propagationTypeMapping[filterValue],
  render: (_, record: any) => {
    const propagation = getPropagation(record);
    return propagation?.type ? <PropagationTag propagation={propagation} /> : <None />;
  },
};

// Used for the bulk edit modal on the Tasks table
export const bulkEditAtlasWorkOrder = () => {
  return {
    ...atlasWorkOrder(),
    key: 'parentId',
    editRule: TASK_EDIT_WORK_ORDER,
  };
};

export const bulkEditAtlasWorkOrderDynamicCheckbox = (disableCheckbox?: boolean) => {
  return {
    ...bulkEditAtlasWorkOrder(),
    disableCheckbox: disableCheckbox,
  };
};

export const recurrenceIcon: TDisplayColumnData = {
  key: 'recurrence',
  width: 30,
  get: (record: any) => record,
  render: (record: any) => {
    const { name, id, status } = record?.maintenanceActivity ?? {};
    if (name) {
      return (
        <Tooltip
          title={
            <span>
              {i18n.t('task.recurringTaskLabel', 'Recurring Task:')}{' '}
              <a href={`/recurring-task/${id}`}>
                {`${name}${status === AtlasGqlMaintenanceActivityStatus.Ended ? i18n.t('task.endedSuffix', ' (Ended)') : ''}`}
              </a>
            </span>
          }
        >
          <RepeatingTaskIcon />
        </Tooltip>
      );
    }
    return null;
  },
};

export const attachments: TDisplayColumnData = {
  key: 'attachments',
  dataIndex: 'attachments',
  title: i18n.t('table.attachments', 'Attachments'),
  render: (attachs: any) => {
    // If we want to add a popover we can reuse the Popover logic in: components/damages/static:taskStatus
    return attachs?.length ? (
      <Popover
        content={
          <StyledTable
            data-testid="attachments-popover-table"
            columns={[ATTACHMENTS_COLUMNS.name, ATTACHMENTS_COLUMNS.category]}
            dataSource={attachs}
            size="small"
            rowKey="id"
            pagination={false}
            scroll={STANDARD_COLUMN_SCROLL_Y}
            style={{ width: TABLE_SCROLL_WIDTH_SMALL }}
          />
        }
      >
        <CenteredSpan data-testid="attachments-count">{attachs.length}</CenteredSpan>
      </Popover>
    ) : (
      <CenteredSpan>
        <None />
      </CenteredSpan>
    );
  },
  onHeaderCell: () => ({
    'data-testid': 'tasks-attachments-header',
  }),
};

/**
 * This function is specific for columns that we need to build based on damage columns
 * that come from the useDamageAttrs hook.
 * @param {array} damageColumns - array of damage columns returned from useDamageAttrs hook
 * @param {boolean} isHorizonDamage - whether to use legacy or horizonDamage attributes
 * @param {string} columnName - name of the targeted attribute in the damageColumn
 * @param {string} horizonDamageAttrTarget - targeted attribute string in the horizonDamage data object.
 *   example: 'horizonDamage.primaryObservationGroupAttrs.Severity'
 * @param {string} legacyDamageAttrsTarget - targeted attribute string in the legacy data object.
 *   example: 'damage.attrs.Severity'
 * @param {Object} overrideProps - used in cases where you need to change any of the properties after everything else.
 *   example: { title: 'Damage Type' } - will change the title to Damage Type
 */
export const damageColumnBuilder = (
  damageColumns: any[],
  isHorizonDamage: boolean,
  columnName: string,
  horizonDamageAttrTarget: string,
  legacyDamageAttrsTarget: string,
  overrideProps?: any
): TDisplayColumnData | undefined => {
  const column = damageColumns.find((column: any) => column.name === columnName);
  const horizonDamageAttrs = horizonDamageAttrTarget;
  const legacyDamageAttrs = legacyDamageAttrsTarget;
  const targetAttrsDataString = isHorizonDamage ? horizonDamageAttrs : legacyDamageAttrs;
  const splitString = isHorizonDamage
    ? horizonDamageAttrs.split('.')
    : legacyDamageAttrs.split('.');

  // return undefined if field not found in damage schema
  return column
    ? {
        ...column,
        onHeaderCell: () => ({
          'data-testid': `damage-${columnName.toLowerCase()}-header`,
        }),
        dataIndex: splitString,
        sorter: (a, b) => {
          // special case for sorting the severity column by severity, then by critical
          if (splitString[splitString.length - 1] === 'Severity') {
            // makes sure that when Severity is not defined,
            // those results appear above other Severity values
            const _a = isNil(get(a, targetAttrsDataString))
              ? set(cloneDeep(a), targetAttrsDataString, Infinity)
              : a;
            const _b = isNil(get(b, targetAttrsDataString))
              ? set(cloneDeep(b), targetAttrsDataString, Infinity)
              : b;

            // finds critical status of target observation group for secondary sort
            const aCritical = !!getObservationGroupByInspectionId(
              _a.horizonDamage,
              getTaskTargetInspectionId(_a.targets) ?? ''
            )?.groupCritical;
            const bCritical = !!getObservationGroupByInspectionId(
              _b.horizonDamage,
              getTaskTargetInspectionId(_b.targets) ?? ''
            )?.groupCritical;

            return sortBy(targetAttrsDataString, 'targetGroupCritical')(
              { ..._a, targetGroupCritical: aCritical },
              { ..._b, targetGroupCritical: bCritical }
            );
          }

          return sortBy(targetAttrsDataString)(a, b);
        },
        filterProperty: targetAttrsDataString,
        onFilter: getOnFilter({
          ...column.config,
          key: targetAttrsDataString,
          exactMatch: true,
        }),
        get: (record: any) => get(record, targetAttrsDataString),
        render: (_: any, record: any) => {
          if (record?.hasOwnProperty('horizonDamage')) {
            return column.render(_, record);
          }

          return <None />;
        },

        ...overrideProps,
      }
    : undefined;
};

export const getTaskDamageColumns = (
  damageColumns: any[],
  isHorizonDamage: boolean,
  columnNames?: string[]
): (TDisplayColumnData | undefined)[] => {
  const finalNames = columnNames ?? damageColumns.map((column) => column.name);

  return finalNames.map((name) => {
    const horizonDamageAttrTarget = `horizonDamage.primaryObservationGroupAttrs.${name}`;
    const legacyDamageAttrsTarget = `damage.attrs.${name}`;
    let overrideProps: { title?: string } = {};
    switch (name) {
      case 'Type':
        overrideProps = {
          title: i18n.t('table.damageType', 'Damage Type'),
        };
        break;
      case 'Monitoring Recommendation':
        overrideProps = { title: i18n.t('table.recommendation', 'Recommendation') };
        break;
    }

    return damageColumnBuilder(
      damageColumns,
      isHorizonDamage,
      name,
      horizonDamageAttrTarget,
      legacyDamageAttrsTarget,
      overrideProps
    );
  });
};

// Used to display the priority number associated with tasks in a work order
export const priorityColumn = (tasks: any): TDisplayColumnData => {
  // Build out the range of possible priorities
  const prioritiesFilterDropdownValues = tasks
    .map((t: any) => {
      // If we don't have a priority, return undefined to filter it out later.
      if (!t.priority) {
        return undefined;
      }
      return { id: t.priority, label: t.priority };
    })
    .filter(Boolean);

  return {
    key: 'priority',
    dataIndex: 'priority',
    title: i18n.t('table.priority', 'Priority'),
    filterProperty: 'priority',
    filterDropdown: getFilterDropdown({
      values: prioritiesFilterDropdownValues,
      type: 'number',
      step: 1,
      includeNoneOption: true,
      noneOptionValue: ' ',
    }),
    onFilter: getOnFilter({
      key: 'priority',
      type: 'number',
      includeNoneOption: true,
      // Properties belowed are unused for this column. getOnFilter expects them.
      filterAttr: undefined,
      exactMatch: true,
      values: undefined,
    }),
    sorter: sortBy('priority'),
    render: (priority: any) => {
      return priority ? priority : <None />;
    },
  };
};

export const forOrgColumn = (tasks: AtlasGqlTask[]): TDisplayColumnData => {
  const options: {
    text: string;
    value: string;
  }[] = uniqBy(
    tasks
      .filter((task): task is AtlasGqlInspectionTask => 'forOrg' in task && !!task.forOrg)
      .map(({ forOrg }) => ({ text: forOrg!.name, value: forOrg!.id })),
    'value'
  );

  return {
    key: 'forOrg',
    dataIndex: 'forOrg',
    title: i18n.t('table.forOrg', 'For Org'),
    filterProperty: 'forOrg.id',
    filters: [...options, { text: <None />, value: ' ' }],
    onFilter: (filter: string, record: AtlasGqlTask) => {
      if (filter !== ' ') {
        return 'forOrg' in record && (record as AtlasGqlInspectionTask).forOrg?.id === filter;
      }
      return !('forOrg' in record) || ('forOrg' in record && !record.forOrg);
    },
    sorter: sortBy('forOrg.name'),
    render: (forOrg: AtlasGqlOrganization) => forOrg?.name ?? <None />,
  };
};

export const inspectionLink: TDisplayColumnData = {
  key: 'inspection',
  dataIndex: ['inspection'],
  title: i18n.t('table.inspection', 'Inspection'),
  get: (record: AtlasGqlInspectionTask) => get(record, ['inspection']),
  render: (inspection: AtlasGqlInspection) => {
    return inspection?.id ? (
      <Link to={`/inspections/${inspection.id}`}>{inspection.id}</Link>
    ) : (
      <None />
    );
  },
};
